import { useState, forwardRef, useEffect } from "react";
import {
  Autocomplete,
  Button,
  TextField,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  CircularProgress,
  Divider,
  LinearProgress,
  InputAdornment,
  Box,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Invoice, resetForm, setDialog } from "../../../../app/slices/invoiceSlice";
import { SelectSupplier } from "../../../../components/Selectors/SelectSupplier";
import { SelectProducts } from "../../../../components/Selectors/SelectProducts";
import { NumericFormat } from "react-number-format";
import { BiTrash } from "react-icons/bi";
import { invoiceTotals, num } from "../../../../helpers/invoiceTotals";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const InvoiceForm = () => {
  const dispatch = useDispatch();

  const _Invoice = useSelector((state) => state.invoice.invoice);
  const _SearchBody = useSelector((state) => state.invoice.searchBody);
  const _Supplier = useSelector((state) => state.supplier.suppliersList.data);
  const _Products = useSelector((state) => state.product.products.data);

  const [loading, setLoading] = useState(false);

  const isAdd = _Invoice.form.id ? false : true;
  const { control, reset, handleSubmit, setValue, watch, getValues } = useForm({
    defaultValues: _Invoice.form,
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  // Called after any price / discount / quantity change; invoiceTotals owns the
  // arithmetic, this only writes the results back onto the form.
  const recalc = () => {
    const { lines, total_amount } = invoiceTotals(
      getValues("items"),
      getValues("discount")
    );
    lines.forEach((line, index) => {
      setValue(`items[${index}].net_price`, line.net_price);
      setValue(`items[${index}].total_amount`, line.total_amount);
    });
    setValue("total_amount", total_amount);
  };

  const onSubmit = (data) => {
    setLoading(true);
    const body = {
      supplier_id: data.supplier_id,
      discount: num(data.discount),
      total_amount: num(data.total_amount),
      items: data.items.map((item) => ({
        product_id: item.product_id,
        quantity: num(item.quantity),
        price: num(item.price),
        discount: num(item.discount),
        net_price: num(item.net_price),
        total_amount: num(item.total_amount),
        serial_numbers: item.serial_numbers ?? [],
      })),
    };
    const action = isAdd
      ? Invoice.create(body, _SearchBody)
      : Invoice.update(data.id, body, _SearchBody);
    dispatch(action)
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    reset({ ..._Invoice.form });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_Invoice.form]);

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A product loaded by the debounced search may no longer be in the list, so
  // fall back to "it already has serials" before hiding the field.
  const isSerialized = (index) => {
    const productId = watch(`items[${index}].product_id`);
    const product = _Products.find((x) => x.id === productId);
    if (product) return product.is_serialized;
    return (watch(`items[${index}].serial_numbers`) ?? []).length > 0;
  };

  return (
    <Dialog
      open={_Invoice.dialog}
      TransitionComponent={Transition}
      keepMounted
      fullWidth={true}
      dir="rtl"
      maxWidth="md"
      onClose={() => dispatch(setDialog())}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogContent>
        <Typography
          mb={3}
          variant="h4"
          color={"purple"}
          gutterBottom
          component="div"
        >
          {isAdd ? "أضافة فاتورة" : "تعديل فاتورة"}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          {_Invoice.loading && <LinearProgress sx={{ marginBottom: 1 }} />}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="supplier_id"
                control={control}
                render={({ field }) => (
                  <SelectSupplier
                    required
                    onChange={(e, newValue) => {
                      setValue("supplier_id", newValue ? newValue.id : null);
                    }}
                    value={
                      _Supplier.filter((x) => x.id === field.value)[0] ?? null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="discount"
                control={control}
                render={({ field }) => (
                  <NumericFormat
                    thousandSeparator
                    customInput={TextField}
                    value={field.value}
                    onChange={(e) => {
                      setValue("discount", num(e.target.value));
                      recalc();
                    }}
                    valueIsNumericString
                    allowNegative={false}
                    fullWidth
                    size="small"
                    label="خصم الفاتورة"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">IQD</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="total_amount"
                control={control}
                render={({ field }) => (
                  <NumericFormat
                    thousandSeparator
                    customInput={TextField}
                    value={field.value}
                    disabled
                    fullWidth
                    size="small"
                    label="المبلغ الكلي بعد الخصم"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">IQD</InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {fields.map((item, index) => {
                  const serials = watch(`items[${index}].serial_numbers`) ?? [];
                  const quantity = num(watch(`items[${index}].quantity`));
                  return (
                    <Grid item xs={12} key={item.id}>
                      <Box
                        sx={{
                          p: 2,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary">
                            {`المادة ${index + 1}`}
                          </Typography>
                          <Tooltip title="حذف المادة">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => {
                                remove(index);
                                recalc();
                              }}
                            >
                              <BiTrash />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <Controller
                              control={control}
                              name={`items[${index}].product_id`}
                              defaultValue={item.product_id}
                              render={({ field }) => (
                                <SelectProducts
                                  required
                                  onChange={(e, newValue) => {
                                    setValue(
                                      `items[${index}].product_id`,
                                      newValue ? newValue.id : null
                                    );
                                    setValue(
                                      `items[${index}].price`,
                                      newValue ? newValue.buying_price : 0
                                    );
                                    if (!newValue?.is_serialized)
                                      setValue(
                                        `items[${index}].serial_numbers`,
                                        []
                                      );
                                    recalc();
                                  }}
                                  value={
                                    _Products.filter(
                                      (x) => x.id === field.value
                                    )[0] ?? null
                                  }
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Controller
                              control={control}
                              name={`items[${index}].price`}
                              defaultValue={item.price}
                              render={({ field }) => (
                                <NumericFormat
                                  thousandSeparator
                                  customInput={TextField}
                                  value={field.value}
                                  onChange={(e) => {
                                    setValue(
                                      `items[${index}].price`,
                                      num(e.target.value)
                                    );
                                    recalc();
                                  }}
                                  valueIsNumericString
                                  allowNegative={false}
                                  fullWidth
                                  size="small"
                                  label="السعر"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Controller
                              control={control}
                              name={`items[${index}].discount`}
                              defaultValue={item.discount}
                              render={({ field }) => (
                                <NumericFormat
                                  thousandSeparator
                                  customInput={TextField}
                                  value={field.value}
                                  onChange={(e) => {
                                    setValue(
                                      `items[${index}].discount`,
                                      num(e.target.value)
                                    );
                                    recalc();
                                  }}
                                  valueIsNumericString
                                  allowNegative={false}
                                  fullWidth
                                  size="small"
                                  label="الخصم"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <Controller
                              control={control}
                              name={`items[${index}].net_price`}
                              defaultValue={item.net_price}
                              render={({ field }) => (
                                <NumericFormat
                                  thousandSeparator
                                  customInput={TextField}
                                  value={field.value}
                                  disabled
                                  fullWidth
                                  size="small"
                                  label="السعر الصافي"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={6} md={1}>
                            <Controller
                              control={control}
                              name={`items[${index}].quantity`}
                              defaultValue={item.quantity}
                              render={({ field }) => (
                                <NumericFormat
                                  thousandSeparator
                                  customInput={TextField}
                                  value={field.value}
                                  onChange={(e) => {
                                    setValue(
                                      `items[${index}].quantity`,
                                      num(e.target.value)
                                    );
                                    recalc();
                                  }}
                                  valueIsNumericString
                                  allowNegative={false}
                                  fullWidth
                                  size="small"
                                  label="العدد"
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Controller
                              control={control}
                              name={`items[${index}].total_amount`}
                              defaultValue={item.total_amount}
                              render={({ field }) => (
                                <NumericFormat
                                  thousandSeparator
                                  customInput={TextField}
                                  value={field.value}
                                  disabled
                                  fullWidth
                                  size="small"
                                  label="المبلغ الكلي"
                                />
                              )}
                            />
                          </Grid>
                          {isSerialized(index) ? (
                            <Grid item xs={12}>
                              <Controller
                                control={control}
                                name={`items[${index}].serial_numbers`}
                                defaultValue={item.serial_numbers ?? []}
                                render={({ field }) => (
                                  <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={field.value ?? []}
                                    onChange={(e, newValue) =>
                                      setValue(
                                        `items[${index}].serial_numbers`,
                                        newValue
                                      )
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label="الارقام التسلسلية"
                                        helperText={
                                          serials.length === quantity
                                            ? `${serials.length}/${quantity}`
                                            : `عدد الارقام التسلسلية ${serials.length} والعدد ${quantity}`
                                        }
                                        error={serials.length !== quantity}
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Grid>
                          ) : null}
                        </Grid>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  append({
                    product_id: null,
                    quantity: 1,
                    price: 0,
                    discount: 0,
                    net_price: 0,
                    total_amount: 0,
                    serial_numbers: [],
                  })
                }
              >
                اضافة منتج جديد
              </Button>
            </Grid>
            <Grid item xs={12} md={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading ? true : false}
                endIcon={loading ? <CircularProgress size={25} /> : null}
              >
                حفظ
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceForm;
